import dayjs from 'dayjs';

import { formatNumbersBasedOnLanguage } from 'utils/intl-wrapper';
import { currentUserCountryCode } from 'constants/countries/countries-mapping';
import { LOCALE } from 'constants/intl-wrapper';
import {
  BOT,
  BOT_CURRENCY,
  COLLECT_ADDRESS_SOURCE,
  LAST_MESSAGE_TIME_FORMAT
} from 'constants/chats';
import { userHasTabAccess } from 'utils/permissions/permissions-apply';
import { PERMISSIONS_MODULES_ID } from 'constants/team-members';
import { generateUUID } from './helpers';
import { sendSegment } from './segment';
import {
  collectShippingInfo,
  markAsSean,
  markAsUnread,
  updateChatStatus
} from 'services/chats';

import { fmt } from 'components/IntlWrapper/IntlWrapper';
import { notify } from 'components/Notify/Notify';

export const getBotCurrency = (cod, botLang) => {
  return BOT_CURRENCY(cod, botLang)[currentUserCountryCode];
};

export const getFormatBotCurrency = (value, botLang = 'ar') => {
  const localizeNumbers = botLang === LOCALE.EN ? 'en-EN' : 'ar-EG';
  return getBotCurrency(
    formatNumbersBasedOnLanguage(value, localizeNumbers),
    botLang
  ).localized;
};

export const handleLastMessageTime = ({
  messageTimestamp,
  chat,
  isAssignNote = false
}) => {
  const today = dayjs();
  const messageDate = dayjs(messageTimestamp);
  if (!dayjs(messageTimestamp).isValid()) {
    return null;
  }
  if (messageDate.isSame(today, 'day')) {
    // If today, show the date and time
    return messageDate.format(LAST_MESSAGE_TIME_FORMAT);
  } else if (messageDate.isSame(today.subtract(1, 'day'), 'day')) {
    // If the day before, show 'Yesterday' and the time
    return `${fmt({ id: 'notification.yesterday' })}  ${
      chat?.timestamp ? messageDate.format(LAST_MESSAGE_TIME_FORMAT) : ''
    }`;
  } else if (messageDate.isAfter(today.subtract(7, 'day'))) {
    // If within the same week, show the day name and the time
    return isAssignNote
      ? messageDate.format(`ddd, D MMM ${LAST_MESSAGE_TIME_FORMAT}`)
      : chat?.timestamp
      ? messageDate.format('ddd, h:mm A')
      : messageDate.format('dddd');
  } else {
    // If 7 days before today, show DD/MM/YY and the time
    return isAssignNote
      ? messageDate.format(`ddd, D MMM YYYY ${LAST_MESSAGE_TIME_FORMAT}`)
      : `${messageDate.format('DD/MM/YYYY')}  ${
          chat?.timestamp
            ? `at ${messageDate.format(LAST_MESSAGE_TIME_FORMAT)}`
            : ''
        }`;
  }
};

export const filterSeenChats = (chats, currentChat) =>
  chats?.filter((chat) => chat?._id !== currentChat?._id);

export const updatedCurrentChatData = (chats, currentChat) =>
  chats?.length && chats?.find((chat) => chat?._id === currentChat.id);

export const removePriceCommaAndConvert = (price) => {
  return typeof price === 'number' ? price : Number(price?.replace(/,/g, ''));
};

export const updateCurrentChatData = (chats, currentChat) =>
  chats?.length && chats?.find((chat) => chat?._id === currentChat?.id);

export const initialAvatar = (name) =>
  name
    ?.split(' ')
    ?.map((word) => word.charAt(0))
    ?.join('')
    ?.toUpperCase();

export const newChatListAfterAssignMember = (chats, currentChat, user) =>
  chats?.map((chat) =>
    chat?._id === currentChat?._id
      ? {
          ...chat,
          assignee: {
            firstName: user?.profile?.firstName,
            lastName: user?.profile?.lastName,
            userId: user?._id
          }
        }
      : chat
  );

//toggiliing read unread

export const handleSelectedChatMarkStatus = ({
  chats,
  numberOfUnreadMessages,
  currentChat,
  setChats,
  setCurrentChat,
  isChatList,
  isFilteredUnReadMessages
}) => {
  const newChats = chats?.map((chat) =>
    chat?._id === currentChat?.id
      ? {
          ...chat,
          unreadMessagesCount: numberOfUnreadMessages,
          seen: !numberOfUnreadMessages
        }
      : chat
  );

  //filtering if i mark chat read when i on filter unread
  if (
    isChatList &&
    isFilteredUnReadMessages &&
    !numberOfUnreadMessages &&
    localStorage.getItem('currentChatId') !== currentChat?.id
  ) {
    setChats((prev) => prev.filter((chat) => chat?._id !== currentChat?.id));
  } else {
    setChats(newChats);
  }

  setCurrentChat((prev) => ({ ...prev, seen: !numberOfUnreadMessages }));
};

export const handleMarkAction = async ({
  numOfUnreadMessages,
  status,
  updatedCurrentChat,
  chats,
  setChats,
  setCurrentChat,
  setTotalUnReadChatsCount,
  isChatList,
  isFilteredUnReadMessages
}) => {
  try {
    const result = !numOfUnreadMessages
      ? await markAsUnread(updatedCurrentChat?.customer?.platforms?.[0], {
          chatId: updatedCurrentChat?.id
        })
      : await markAsSean({
          chatId: updatedCurrentChat?.id,
          platform: updatedCurrentChat?.customer?.platforms?.[0],
          messengerUserId: updatedCurrentChat?.customer?.messengerUserId
        });

    if (result?.message) {
      if (updatedCurrentChat?.unreadMessagesCount) {
        handleSelectedChatMarkStatus({
          chats,
          numberOfUnreadMessages: 0,
          currentChat: updatedCurrentChat,
          setChats,
          setCurrentChat,
          isChatList,
          isFilteredUnReadMessages
        });
        if (setTotalUnReadChatsCount) {
          setTotalUnReadChatsCount((prev) => ({
            ...prev,
            unreadChats: prev.unreadChats - 1,
            [updatedCurrentChat?.customer?.platforms?.[0]]:
              prev[updatedCurrentChat?.customer?.platforms?.[0]] - 1
          }));
        }
      } else {
        handleSelectedChatMarkStatus({
          chats,
          numberOfUnreadMessages: 1,
          currentChat: updatedCurrentChat,
          setChats,
          setCurrentChat
        });
        if (setTotalUnReadChatsCount) {
          setTotalUnReadChatsCount((prev) => ({
            ...prev,
            unreadChats: prev.unreadChats + 1,
            [updatedCurrentChat?.customer?.platforms?.[0]]:
              prev[updatedCurrentChat?.customer?.platforms?.[0]] + 1
          }));
        }
      }
    }
  } catch (error) {
    notify({ msg: error.message, error });
  }
};

export const userHasChatsAccess = (businessInfo = {}) => {
  const { featuresConfigurations = {}, userSllrPermissions = {} } =
    businessInfo;
  const { isInstagramEnabled = false, isMessengerEnabled = false } =
    featuresConfigurations?.sllrChatsConfigurations || {};

  return (
    (isInstagramEnabled || isMessengerEnabled) &&
    userHasTabAccess(PERMISSIONS_MODULES_ID.CHATS, userSllrPermissions)
  );
};

export const getSucessAction = ({
  action,
  setisManageCartVisible,
  handleContainerSliding,
  isContainerSliding,
  setActionMessageState,
  openCart = true
}) => {
  switch (action) {
    case 'ADD_TO_CART':
      notify({
        msg: fmt({ id: 'chats_plugins.chats_page.success_add_to_cart' }),
        type: 'success'
      });
      setActionMessageState(null);
      if (openCart) {
        setisManageCartVisible(true);
        if (!isContainerSliding) handleContainerSliding();
      }
      break;
    default:
      break;
  }
};

export const resetMessageActionState = (
  setActionMessageState,
  messageID = 'GENERAL'
) => {
  setTimeout(() => {
    setActionMessageState((prevState) => ({
      ...prevState,
      [messageID]: null
    }));
  }, 4000);
};

export const formatTextBreaks = (text) => text.replace(/\n/g, '<br/>');

export const platformAccess = (businessInfo) => {
  const userHaveChatAccess =
    businessInfo?.featuresConfigurations?.sllrChatsConfigurations;

  return {
    instagram: userHaveChatAccess?.isInstagramEnabled || false,
    messenger: userHaveChatAccess?.isMessengerEnabled || false
  };
};

export const checkIf24HoursPassed = (timestamp) => {
  if (!timestamp) return true;
  const currentTime = Date.now();
  const timestampInMs = timestamp * 1000;
  const twentyFourHoursInMs = 24 * 7 * 60 * 60 * 1000;

  return currentTime - timestampInMs >= twentyFourHoursInMs;
};

export const handleCollectShippingInfo = async ({
  setIsBotLoading = () => {},
  setActionMessageState = () => {},
  setisManageCartVisible = () => {},
  currentChat = {}
}) => {
  const userInfo = JSON.parse(localStorage?.getItem('userInfo'))?.user;
  const platform = currentChat?.platform;

  const payloadData = {
    businessId: userInfo?.businessAdminInfo?.businessId,
    metaUserId: currentChat?.customer?.messengerUserId,
    customerName: currentChat?.customer?.fullName,
    timestamp: dayjs().utc().unix(),
    clientMessageId: generateUUID()
  };

  try {
    setIsBotLoading?.(true);
    setActionMessageState((prevState) => ({
      ...prevState,
      GENERAL: 'COLLECT_ADDRESS'
    }));
    setisManageCartVisible?.(false);
    const result = await collectShippingInfo(platform, payloadData);
    if (result?.message) {
      setActionMessageState((prevState) => ({
        ...prevState,
        GENERAL: null
      }));
    }
    sendSegment('SLLR_CHAT_COLLECT_ADDRESS', {
      CHANNEL: platform,
      TRIGGER: BOT,
      TRIGGER_SOURCE: COLLECT_ADDRESS_SOURCE.ACTION_MENU,
      EVENT_DATE: dayjs().format('YYYY-MM-DD'),
      BUSINESS_ID: userInfo?.businessAdminInfo?.businessId
    });
  } catch (error) {
    notify({ msg: error.message, error });
  } finally {
    setIsBotLoading?.(false);
  }
};
